import { PATH, PATH_TO_TITLE } from '@repo/constants';
import { useBreadcrumbStore } from '@repo/lib';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Separator,
} from '@ui/components';
import { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';

type BreadcrumbSegment = {
  text: string;
  isLink?: boolean;
  href?: string;
};

const formatPathSegment = (segment: string) => {
  return decodeURIComponent(segment).replace(/-/g, ' ');
};

export const AppBreadcrumb = () => {
  const location = useLocation();
  const labels = useBreadcrumbStore((state) => state.labels);

  const breadcrumbSegments = useMemo(() => {
    if (location.pathname === PATH.DASHBOARD) {
      return [];
    }

    const paths = location.pathname.split('/').filter(Boolean);
    const segments: BreadcrumbSegment[] = [
      { text: 'Dashboard', isLink: true, href: PATH.DASHBOARD },
    ];

    if (paths[0]) {
      const firstLevelPath = `/${paths[0]}`;
      const isNotificationsPath = firstLevelPath === PATH.NOTIFICATIONS;
      const firstLevelText = isNotificationsPath
        ? 'Notifications'
        : formatPathSegment(paths[0]);
      segments.push({
        text: firstLevelText,
        ...(isNotificationsPath && paths.length > 1
          ? { isLink: true, href: PATH.NOTIFICATIONS }
          : {}),
      });
    }

    const secondLevelPath = `/${paths[0]}/${paths[1]}`;
    if (paths[1] && PATH_TO_TITLE[secondLevelPath]) {
      segments.push({
        text: PATH_TO_TITLE[secondLevelPath],
        isLink: paths[2] !== '0',
        href: `/${paths[0]}/${paths[1]}`,
      });
    } else if (
      paths[1] &&
      !PATH_TO_TITLE[secondLevelPath] &&
      paths.length === 2
    ) {
      const fullPath = `/${paths[0]}/${paths[1]}`;
      segments.push({
        text: labels[fullPath] || formatPathSegment(paths[1]),
        isLink: false,
      });
    }

    const skipDetailsSegment =
      paths[0] === 'reports' &&
      paths[1] === 'portfolios' &&
      paths[2] === 'details';

    const remainingPaths = paths.slice(2);
    remainingPaths.forEach((path, index) => {
      if (skipDetailsSegment && path === 'details') return;
      if (path && path !== '0') {
        const fullPath = `/${paths.slice(0, index + 3).join('/')}`;
        segments.push({
          text: labels[fullPath] || formatPathSegment(path),
          isLink: false,
        });
      }
    });

    return segments;
  }, [location.pathname, labels]);

  if (location.pathname === PATH.DASHBOARD) {
    return null;
  }

  return (
    <>
      <Separator
        className="h-5"
        orientation="vertical"
      />
      <Breadcrumb>
        <BreadcrumbList>
          {breadcrumbSegments.map((segment, index) => (
            <BreadcrumbItem key={index}>
              {index > 0 && <BreadcrumbSeparator />}
              {segment.isLink ? (
                <BreadcrumbLink
                  className="text-foreground"
                  asChild
                >
                  <Link to={segment.href!}>{segment.text}</Link>
                </BreadcrumbLink>
              ) : (
                <BreadcrumbPage className="capitalize text-foreground">
                  {segment.text}
                </BreadcrumbPage>
              )}
            </BreadcrumbItem>
          ))}
        </BreadcrumbList>
      </Breadcrumb>
    </>
  );
};
